import { UserInfo } from "@shared/models";
import { Member } from "~/store";
import { Group } from "~/types/group";

const API_BASE_URL = "/api";

// Interface for the response with typed date fields
export interface UserHomeDataResponse {
  user: UserInfo;
  defaultGroup: Group | null;
  groupMembers: Array<{
    id: number;
    groupId: number;
    role: string;
    joinedAt: Date;
    addedByUserId: number;
    member: Member;
  }>;
}

// Interface for the raw API response (with string dates)
interface RawUserHomeDataResponse {
  user: UserInfo;
  defaultGroup: Group | null;
  groupMembers: Array<{
    id: number;
    groupId: number;
    role: string;
    joinedAt: string; // Date as string from API
    addedByUserId: number;
    member: Member;
  }>;
}

/**
 * Fetch all data needed for the user's home page
 * @returns Promise resolving to the user home data
 */
export const fetchUserHomeData = async (): Promise<UserHomeDataResponse | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/home-data`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Failed to fetch user home data" }));
      console.error("Error fetching user home data:", errorData);
      return null;
    }

    const rawData = (await response.json()) as RawUserHomeDataResponse;

    // Convert the raw data to our expected format with Date objects
    const convertedData: UserHomeDataResponse = {
      user: rawData.user,
      defaultGroup: rawData.defaultGroup,
      groupMembers: rawData.groupMembers.map((gm) => ({
        ...gm,
        joinedAt: new Date(gm.joinedAt)
      }))
    };

    return convertedData;
  } catch (error) {
    console.error("Exception in fetchUserHomeData:", error);
    return null;
  }
};
